import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import {
  Button,
  Icon,
  Loader,
  Pagination,
  Popup,
  Table,
} from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { EmailLogModal } from "components/lib/EmailLogModal";
import { EmptyBox } from "components/lib/UI";

import type { IdeaPreviewIdea } from "../../types";
import IdeaEmailModal from "./IdeaEmailModal";

type EmailManagementProps = { idea: IdeaPreviewIdea };

const EmailManagement = ({ idea }: EmailManagementProps) => {
  const [page, setPage] = useState(1);
  const { t } = useTranslation();
  const logsQuery = useQuery(
    initUseQuery(api.ideas.getEmailLogs, [idea?._id, page]),
  );
  const [isLoadingLog, setIsLoadingLog] = useState("");
  const [activeLog, setActiveLog] = useState<
    OpenAPI.Schemas["EmailLog"] | null
  >(null);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const user = useAppSelector((state) => state.user);
  const challengeId = idea?.challenge;

  const canViewProjectEmails = util.hasPermission(
    user!,
    "challenge.viewProjectEmails",
    challengeId,
  );
  const canSendProjectEmails = util.hasPermission(
    user!,
    "challenge.sendProjectEmails",
    challengeId,
  );
  const canManage = util.canManageChallenge(user!, idea?.ownerChallenge);
  if (!canViewProjectEmails && !canSendProjectEmails && !canManage) {
    return null;
  }
  return (
    <div>
      <EmailLogModal log={activeLog} onClose={() => setActiveLog(null)} />
      <IdeaEmailModal
        idea={idea}
        isOpen={sendEmailModalOpen}
        setOpen={setSendEmailModalOpen}
      />
      {(canSendProjectEmails || canManage) && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={() => setSendEmailModalOpen(true)}
            icon="send"
            content="Send new email"
          />
        </div>
      )}
      {(canViewProjectEmails || canManage) && (
        <>
          {logsQuery.isLoading ?
            <Loader active inline="centered" />
          : !logsQuery?.data?.logs?.length ?
            <EmptyBox>
              No email communication has been sent regarding this{" "}
              {t("generic.idea")}.
            </EmptyBox>
          : <>
              <Table compact size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Recipient</Table.HeaderCell>
                    <Table.HeaderCell>Date</Table.HeaderCell>
                    <Table.HeaderCell>Subject</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell></Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {logsQuery?.data?.logs.map((log) => (
                    <Table.Row key={log._id}>
                      <Table.Cell>
                        {log.ownerUser ?
                          <UserChip user={log.ownerUser} compact="very" />
                        : log.recipientEmail}
                      </Table.Cell>
                      <Table.Cell>
                        {formatDate(log.sentAt || log.createdAt, "dd/MM/yy")}
                      </Table.Cell>

                      <Table.Cell>
                        {log.subject.slice(0, 23)}
                        {log.subject.length > 22 && "..."}
                      </Table.Cell>

                      <Table.Cell collapsing>
                        {!log.sent ?
                          <Popup
                            trigger={<Icon name="wait" color="yellow" />}
                            content={<span>Awaiting delivery</span>}
                          />
                        : (
                          log.sent &&
                          (log.events?.delivered || log.events?.opened)
                        ) ?
                          <Popup
                            trigger={<Icon name="check circle" color="green" />}
                            content={
                              <span>
                                {(log.events?.opened && "Opened") ||
                                  (log.events?.delivered && "Delivered")}
                              </span>
                            }
                          />
                        : (
                          log.sent &&
                          (log.events?.bounced || log.events?.dropped)
                        ) ?
                          <Popup
                            trigger={
                              <span>
                                <Icon name="warning circle" color="red" />
                              </span>
                            }
                            content={
                              <>
                                <p>{`Status: ${log.events?.bounced ? "Bounced" : "Undeliverable"} `}</p>
                                <p>
                                  We were unable to deliver this message. Please
                                  double check the recipient&apos;s email
                                  address and try again. If this issue persists
                                  please contact us.
                                </p>
                              </>
                            }
                          />
                        : <Popup
                            trigger={
                              <span>
                                <Icon
                                  name="check circle outline"
                                  color="green"
                                />
                              </span>
                            }
                            content={
                              <>
                                <p>Status: Sent </p>
                                <p>
                                  This email was sent but we have not received
                                  any further delivery information.
                                </p>
                              </>
                            }
                          />
                        }
                      </Table.Cell>

                      <Table.Cell collapsing>
                        <Button
                          loading={isLoadingLog === log._id}
                          onClick={() => {
                            setIsLoadingLog(log._id);
                            api.ideas.getEmailLog(
                              idea?._id,
                              log._id,
                              (response) => {
                                setIsLoadingLog("");
                                setActiveLog(response.log);
                              },
                              () => {
                                setIsLoadingLog("");
                              },
                            );
                          }}
                        >
                          View
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              <Pagination
                activePage={page}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                totalPages={logsQuery?.data?.totalPages || 1}
                onPageChange={(_e, { activePage }) => {
                  setPage(activePage as number);
                }}
              />
            </>
          }
        </>
      )}
    </div>
  );
};

export default EmailManagement;
